import React from 'react';
import * as Sentry from '@sentry/react';

export interface PrivateOutletErrorFallbackProps {
  error: Error | unknown;
  resetError: () => void;
  componentStack?: string | null;
  eventId?: string | null;
}

export const PrivateOutletErrorFallback: React.FC<PrivateOutletErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
      <div className="max-w-xl w-full shadow-md rounded-lg p-6 space-y-4">
        <div className="flex items-start space-x-4">
          <div className="text-4xl">⚠️</div>
          <div className="space-y-2">
            <h1 className="text-xl font-bold">Something went wrong</h1>
            <p className="text-sm">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred.'}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={resetError}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
};

export interface PrivateOutletBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
}

export const PrivateOutletBoundary: React.FC<PrivateOutletBoundaryProps> = ({
  children,
  onReset,
}) => {
  return (
    <Sentry.ErrorBoundary
      onReset={onReset}
      fallback={({ error, resetError, componentStack, eventId }) => (
        <PrivateOutletErrorFallback
          error={error}
          resetError={() => {
            if (onReset) onReset();
            resetError();
          }}
          componentStack={componentStack}
          eventId={eventId}
        />
      )}
      onError={(error, componentStack, eventId) => {
        if (import.meta.env.DEV) {
          console.error(
            '[PrivateOutletBoundary] Caught rendering error:',
            error,
            componentStack,
            'Sentry Event ID:',
            eventId,
          );
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default PrivateOutletBoundary;
